import React, { useCallback, useEffect, useRef, useState } from 'react';

// Roda de cores HSV: ângulo = matiz, distância do centro = saturação, slider = brilho.

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const hsvToHex = (h: number, s: number, v: number): string => {
    const f = (n: number) => {
        const k = (n + h / 60) % 6;
        return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
    };
    const to = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${to(f(5))}${to(f(3))}${to(f(1))}`;
};

export const hexToHsv = (hex: string): { h: number; s: number; v: number } => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return { h: 0, s: 0, v: 1 };
    const n = parseInt(m[1], 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), d = max - Math.min(r, g, b);
    let h = 0;
    if (d) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
    }
    return { h, s: max ? d / max : 0, v: max };
};

interface ColorWheelProps {
    value: string;
    onChange: (hex: string) => void;
    size?: number;
    brightnessLabel?: string;
}

export const ColorWheel: React.FC<ColorWheelProps> = ({ value, onChange, size = 220, brightnessLabel }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hsv, setHsv] = useState(() => hexToHsv(value));
    const dragging = useRef(false);

    // Sincroniza quando a cor muda por fora (ex.: campo hex), sem perder a matiz em cinzas.
    useEffect(() => {
        if (hsvToHex(hsv.h, hsv.s, hsv.v).toLowerCase() !== value.toLowerCase()) setHsv(hexToHsv(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        const r = size / 2;
        ctx.clearRect(0, 0, size, size);
        // Matiz em fatias finas (sem serrilhado) e depois um degradê radial branco para a saturação.
        for (let a = 0; a < 360; a += 1) {
            ctx.beginPath();
            ctx.moveTo(r, r);
            ctx.arc(r, r, r, ((a - 90 - 0.6) * Math.PI) / 180, ((a - 90 + 0.6) * Math.PI) / 180);
            ctx.closePath();
            ctx.fillStyle = `hsl(${a}, 100%, 50%)`;
            ctx.fill();
        }
        const white = ctx.createRadialGradient(r, r, 0, r, r, r);
        white.addColorStop(0, 'rgba(255,255,255,1)');
        white.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = white;
        ctx.beginPath();
        ctx.arc(r, r, r, 0, Math.PI * 2);
        ctx.fill();
    }, [size]);

    const pick = useCallback((clientX: number, clientY: number) => {
        const rect = canvasRef.current!.getBoundingClientRect();
        const r = rect.width / 2;
        const dx = clientX - rect.left - r;
        const dy = clientY - rect.top - r;
        const s = clamp(Math.hypot(dx, dy) / r, 0, 1);
        const h = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        const next = { h: (h + 360) % 360, s, v: hsv.v || 1 };
        setHsv(next);
        onChange(hsvToHex(next.h, next.s, next.v));
    }, [hsv.v, onChange]);

    const angle = ((hsv.h - 90) * Math.PI) / 180;
    const knobX = size / 2 + Math.cos(angle) * hsv.s * (size / 2);
    const knobY = size / 2 + Math.sin(angle) * hsv.s * (size / 2);
    const current = hsvToHex(hsv.h, hsv.s, hsv.v);

    return (
        <div className="flex flex-col items-center gap-4 select-none">
            <div className="relative" style={{ width: size, height: size }}>
                <canvas
                    ref={canvasRef}
                    style={{ width: size, height: size, filter: `brightness(${0.15 + hsv.v * 0.85})` }}
                    className="rounded-full cursor-crosshair touch-none ring-1 ring-black/10 dark:ring-white/10"
                    onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); pick(e.clientX, e.clientY); }}
                    onPointerMove={(e) => { if (dragging.current) pick(e.clientX, e.clientY); }}
                    onPointerUp={() => { dragging.current = false; }}
                    onPointerCancel={() => { dragging.current = false; }}
                />
                <span
                    className="pointer-events-none absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-[3px] border-white shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
                    style={{ left: knobX, top: knobY, backgroundColor: current }}
                />
            </div>
            <label className="w-full flex flex-col gap-1.5">
                {brightnessLabel && <span className="text-[10px] uppercase font-mono tracking-[0.14em] text-muted">{brightnessLabel}</span>}
                <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={hsv.v}
                    onChange={(e) => {
                        const next = { ...hsv, v: parseFloat(e.target.value) };
                        setHsv(next);
                        onChange(hsvToHex(next.h, next.s, next.v));
                    }}
                    className="w-full cursor-pointer appearance-none h-2.5 rounded-full accent-ink [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-black/20 [&::-webkit-slider-thumb]:shadow"
                    style={{ background: `linear-gradient(90deg, #000, ${hsvToHex(hsv.h, hsv.s, 1)})` }}
                />
            </label>
        </div>
    );
};

export default ColorWheel;
